import { Injectable, OnInit } from '@angular/core';
import { BehaviorSubject} from 'rxjs';
import { timeout} from 'rxjs/operators';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Platform, NavController, ModalController } from '@ionic/angular';
import {  HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { HttpService } from './http.service';
import { HttpFunctionResult } from '../_interfaces/HttpFunctionResult';
import { StorageService } from './storage.service';
import { StorageManagerService } from './storage-manager.service';
import { FeedbackService, MsgType } from './feedback.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { LicenseService } from './license.service';
import { LicenseLogin } from '../_interfaces/LicenseLogin';
import { AvailableResult, BiometryType, NativeBiometric } from "capacitor-native-biometric";
import { NotificationService } from './notifications/notification.service';
import OneSignal from 'onesignal-cordova-plugin';
import { PaymentService } from './payment/payment.service';


const helper = new JwtHelperService()
const TOKEN_KEY = 'jwt-token'
const REFRESH_TOKEN_KEY = "jwt-refresh-token"

export enum ROLE{
  customer,
  employee
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private userData = new BehaviorSubject(null)
  private _authorizeText: string = ""
  private _isBiometricAuth: boolean = false
  private _role: ROLE 

  constructor(
    public http: HttpService,
    private storageSrv: StorageService,
    private storageManager: StorageManagerService,
    private deviceInformation: DeviceDetectorService,
    private navCrtl: NavController,
    private licenseSrv: LicenseService,
    private platform: Platform,
    private paymentSrv: PaymentService,
    private modalCtrl: ModalController,
    private feedback: FeedbackService
    ) { 
      this.platform.ready().then(async ()=> {
        await this.loadIsActiveBiometricAuth()
        await this.setBiometricText()
      })
    }
  

  public secondSplashAnimation: boolean = false
  public isSecondSplash: boolean = true

  public get userNameFromToken(): string{
    const decoded: {firstName?: string, lastName?: string} = helper.decodeToken(this.http.token)
    if(!decoded) return ""
    return `${decoded?.firstName} ${decoded?.lastName}`
  }

  public get authorizeText(): string{
    return this._authorizeText
  }

  public get isBiometricAuth(): boolean{
    return this._isBiometricAuth
  }

  public get role(): ROLE{
    return this._role
  }
  
  private _isDemoMode : boolean;
  public get isDemoMode() : boolean {
    return this._isDemoMode;
  }
  public set isDemoMode(v : boolean) {
    this._isDemoMode = v;
  }

  public includesRole(role: string): boolean{
    const roles: string[] | string = this.getUser()?.role
    if(typeof roles == "string") return role == roles
    else return roles?.includes(role)
  }

  public set tokenForSinglePage(token: string){
    try {
      let decoded = helper.decodeToken(token)
      this.userData.next(decoded)
      this.http.refreshToken = token
    } catch (error) {
      this.feedback.showError("Der angegebene Token hat ein falsches Format.")
    }
  }

  public async loadStorageToken(customPath?: string){
    // await this.feedback.updateLoading("Laden...")
    const token: string = await this.storageSrv.load(TOKEN_KEY);
    const refreshToken: string = await this.storageSrv.load(REFRESH_TOKEN_KEY)
    // await this.feedback.hideLoading()
    if(token){
      if(helper.isTokenExpired(refreshToken)){
        await this.deleteLoginInformations()
        await this.navCrtl.navigateRoot("login")
        return
      } 
      let decoded = helper.decodeToken(token)
      this.userData.next(decoded)
      this.http.token = token
      this.http.refreshToken = refreshToken
      if(decoded.role == "customer"){
        this._role = ROLE.customer
        if(customPath) this.navCrtl.navigateRoot(customPath)
        else this.navCrtl.navigateRoot("/customer")
      }else{
        this._role = ROLE.employee
        if(customPath) this.navCrtl.navigateRoot(customPath)
        else this.navCrtl.navigateRoot("/tabs/home")
      }
      return true
    }else return null
  }


  async login(credentials: {username: string, password: string, deviceDescription?: string}, stay: boolean, isStandalone: boolean = false): Promise<HttpFunctionResult<number>> {
    this._role = ROLE.employee
    return new Promise(async resolve=>{
      credentials.deviceDescription = this.platform.is("capacitor") ? this.deviceInformation.os : `${this.deviceInformation.os} (${this.deviceInformation.browser})`

      if(this.platform.is("capacitor")) credentials.deviceDescription = this.deviceInformation.os
      else if(this.platform.is(("electron"))) credentials.deviceDescription = `${this.deviceInformation.os} (Desktop)` 
      else credentials.deviceDescription = `${this.deviceInformation.os} (${this.deviceInformation.browser})`

      await this.http.POST<
      {username: string, password: string, deviceDescription?: string},
      {type?: string, accessToken?: string, refreshToken?: string}
      >({
        path: "user/login",
        handleError: false,
        body: credentials,
        timeout: this.isDemoMode ? 10000 : 5000,
        statusResult: async (status: number, result: HttpFunctionResult<{type?: string, accessToken?: string, refreshToken?: string}>, err: HttpErrorResponse)=>{
          if(result.success){
            const token: string = result.data.accessToken
            const refreshToken: string = result.data.refreshToken
            this.http.token = token
            this.http.refreshToken = refreshToken
    
            this.userData.next(helper.decodeToken(token))
            await this.storageSrv.save(TOKEN_KEY, token)
            if (stay) await this.storageSrv.save(REFRESH_TOKEN_KEY, refreshToken)
            return resolve({success: true})
          }else{
            //Wenn 2 Geräte für den Account besetzt sind...
            //sonst normalen error zurück geben...

            if(status == 403){
              await this.feedback.hideLoading()
              if(result.data.type == "devices")
                return resolve(await this.handleDeviceList(err))
              else if(result.data.type == "license"){
                if(isStandalone) this.openPortal(credentials.username, credentials.password)
                await this.feedback.showError(result.message)
              }
            } 
            return resolve({success: false, message: result?.message || "Beim Anmelden ist ein unbekannter fehler aufgetreten", data: status })
          }
        }
      })
    })
  }

  private async handleDeviceList(error: HttpErrorResponse): Promise<HttpFunctionResult<number>>{
    return new Promise(async resolve=>{
      if(!error.error.data) resolve({success: false})
      this.licenseSrv.releaseLicense(error.error.data.devices as LicenseLogin[], async (result: HttpFunctionResult<LicenseLogin>)=>{
        if(result.success) {
          await this.feedback.updateLoading("Löschen...")
          const deleteResponse: HttpFunctionResult<any> = await this.http.DELETE<any>({path: `user/license/devices/${result.data.id}`})
          if(deleteResponse.success) resolve({success: true, data: 403})
          else resolve(deleteResponse)
        }
        else resolve({success: false, data: 403})
      })
    })
  }

  public getUser(): { deviceDescription: string; deviceUID: string; exp: number; firstName: string; iat: number; id: number; iss: string; lastName: string; nbf: number; role: string | string[]; userName: string; }{
    const jwt = this.userData.getValue()
    return {...jwt, id: +jwt?.id}; 
  }

  public async logout(releaseDevice: boolean = true){
    await this.modalCtrl.dismiss().catch(()=>{})
    // if(!releaseDevice) await this.feedback.updateLoading("Abmelden...")
    if(releaseDevice) await this.releaseDevice()
    await this.toggleActiveBiometric(false)
    await this.deleteLoginInformations()
    if(this.platform.is("capacitor"))await this.clearNotificationStorage()
    await this.feedback.hideLoading()
    await this.navCrtl.navigateRoot('login'); 
    await this.feedback.hideLoading()
  }
 
  private async deleteLoginInformations(){
    await this.storageSrv.remove(TOKEN_KEY)
    await this.storageSrv.remove(REFRESH_TOKEN_KEY)
    await this.storageSrv.remove("FIRST_LOGIN_NOTIFICATION_REQUESTED")
    this.userData.next(null)
    this.http.token = ""
    this.http.refreshToken = ""
  }

  /**
   * Gibt das aktuell genutzte Gerät des Benutzers frei.
   * @returns Ein Promise, das bei Erfolg keine Daten zurückgibt.
   */
  public async releaseDevice(): Promise<any>{
    return await this.http.DELETE<any>({
      path: "user/licenses/devices",
      timeout: 6000
    })
  }

  public showLoadingScreen(){
    this.secondSplashAnimation = false
    this.isSecondSplash = true
  }

  public hideLoadingScreen(delay: number = 1000){
    setTimeout(() => {
      this.secondSplashAnimation = true
      setTimeout(() => {
        this.isSecondSplash = false
      }, 1000);
    }, delay);
  }

  private async openPortal(username: string, password: string){
    const confirmResult: boolean = await this.feedback.confirm("Möchtest du das Portal öffnen? Dort können Sie Ihr Abo verwalten.", "Portal öffnen", "Abbrechen", "Portal öffnen?")
    if(!confirmResult) return

    let portalResult: HttpFunctionResult<{url: string}>
    await this.feedback.showLoading(async () => {
      portalResult = await this.paymentSrv.createPortalSessionWithCredetials("portal", {username: username, password: password})
    }, "Portal wird geöffnet...")

    if(portalResult.success) window.open(portalResult.data.url, "_blank")

  }


  private async clearNotificationStorage(){
    await this.storageManager.delete("PUSH_ROOT_TAG")
    await this.storageManager.delete("PUSH_ALLOCATION_TAG")
    await this.storageManager.delete("PUSH_CONTENT_TAG")
    await this.storageManager.delete("PUSH_STATE_TAG")
    await this.storageManager.delete("PUSH_APPOINTMENT_TAG")
    await this.storageManager.delete("PUSH_DESKTOP_TAG")
    await this.storageManager.delete("PUSH_WORKING_TIMES")
    OneSignal.disablePush(true)
    OneSignal.deleteTags(["OnAppointmentChange", "OnStateChange", "OnContentAdd", "OnAllocationChange", "userId", "OnDesktopChange"])
  }

  //#region BIOMETRIC AUTH
  private async setBiometricText(){
    if(!this.platform.is("capacitor")) return
    const result: AvailableResult = await NativeBiometric.isAvailable()
    if(!result.isAvailable) return
    const bioType: BiometryType = result.biometryType
    switch (bioType) {
      case BiometryType.FACE_AUTHENTICATION: //Android Gesichtserkennung
        this._authorizeText = "Gesichtserkennung"
        break;
      case BiometryType.FACE_ID: //IOS Gesichtserkennung
        this._authorizeText = "Face ID"
        break;
      case BiometryType.FINGERPRINT: //Android Fingerabdruck 
        this._authorizeText = "Fingerabdruck"
        break;
      case BiometryType.IRIS_AUTHENTICATION: //Android Iris Scann
        this._authorizeText = " Iris-Scan"
        break;
      case BiometryType.TOUCH_ID: //IOS Fingerabdruck
        this._authorizeText = "Touch ID"
        break;
      default:
        break;
    }
  }

  public async checkBiometricAuth(): Promise<boolean>{
    if(!this.platform.is("capacitor")) return false
    const availableResult: AvailableResult = await NativeBiometric.isAvailable()
    
    if(!availableResult.isAvailable) return false
    return new Promise<boolean>(resolve=> NativeBiometric.verifyIdentity({ reason: "Für sicheren Login", title: "Anmelden"}).then(async()=>resolve(true), async (err)=> resolve(false)))
  }

  private async loadIsActiveBiometricAuth(){
    const isBiometricAuth: boolean = await this.storageManager.getItem<boolean>("ISBIOMETRIC_AUTH")
    if(isBiometricAuth == null) return
    this._isBiometricAuth = isBiometricAuth
  }

  public async toggleActiveBiometric(toggleTo?: boolean){
    this._isBiometricAuth = toggleTo == null ? !this._isBiometricAuth : toggleTo
    await this.storageManager.setItem<boolean>("ISBIOMETRIC_AUTH", this._isBiometricAuth)
  }

  //#endregion

  //#region PASSWORD RECOVERY

  /**
   * Sendet die Mail zum Zurücksetzen seines Passworts 
   * @param {string} email Hinterlegte E-Mail in Manager oder Ticketsystem an die MAil geschickt werden soll 
   * @returns 
   */
  public async sendPasswordRecoveryMail(email: string): Promise<HttpFunctionResult<any>>{
    return await this.http.POST<any, any>({
      path: `user/passwordrecovery/sendmail?mail=${email}&url=${this.http.getbaseUrl.replace("/api/", "")}`,
      body: {},
      withAuth: false
    })
  }

  /**
   * Updatet das Passwort des Benurtzers
   * @param {string} password neues Passwort des Benutzers 
   * @returns 
   */
  public updateUserPassword(password: string): Promise<HttpFunctionResult<any>>{
    return new Promise<HttpFunctionResult<any>>(async resolve=>{
      await this.http.PUT<any, any>({
        path: `user/passwordrecovery/update`,
        body: {username: "", deviceDescription: "", password: password},
        handleError: false,
        statusResult: async (status: number, result: HttpFunctionResult<any>, error: HttpErrorResponse)=>{
          if(status == 401){
            await this.feedback.showError("Session abgelaufen. Bitte führen Sie den Wiederherstellungsprozess erneut durch.")
            return resolve({success: false})
          }
          if(result.success) return resolve(result)
          else return await this.http.handleError(error)
        }
      })
    })
  }


  //#endregion

  //#region CUSTOMER

  /**
   * Updatet Passwort und Anmeldename von Kunden
   * @param {{username: string, password: string}} credentials Benutzername und Passwort des Kunden
   * @returns 
   */
  public setCustomerPassword(credentials: {username: string, password: string}): Promise<HttpFunctionResult<any>>{
    return new Promise(async resolve=>{

      await this.http.PUT<any, any>({
        path: "user/customer/credantials",
        body: credentials,
        handleError: false,
        statusResult: async (status: number, result: HttpFunctionResult<any>, error: HttpErrorResponse)=>{
          if(status == 401){
            await this.feedback.showError("Session abgelaufen. Bitte führen Sie den Wiederherstellungsprozess erneut durch.")
            return resolve({success: false})
          }
          if(result.success) return resolve(result)
          else return await this.http.handleError(error)
        }
      })
    })
  }


  /**
   * Meldet einen Kunden an 
   * @param {{username: string, password: string}} credentials  Anmeldedaten des Kunden
   * @param {boolean} stay sagt ob Kunde angemeldet bleiben soll
   * @returns 
   */
  public async loginCustomer(credentials: {username: string, password: string}, stay: boolean): Promise<HttpFunctionResult<any>>{
    this._role = ROLE.customer

    const result: HttpFunctionResult<{accessToken: string, refreshToken: string}> = await this.http.POST<
    {username: string, password: string},
    {accessToken: string, refreshToken: string}
    >({
      path: "user/login/customer",
      body: credentials,
    })

    if(result.success){
      const token: string = result.data.accessToken
      const refreshToken: string = result.data.refreshToken
      this.http.token = token
      this.http.refreshToken = refreshToken

      this.userData.next(helper.decodeToken(token))
      await this.storageSrv.save(TOKEN_KEY, token)
      if (stay) await this.storageSrv.save(REFRESH_TOKEN_KEY, refreshToken)
    }

    return result
  }

  //#endregion
}


